// ScanStatusBar — vertical list of configured search terms with status icons.
//
// One row per term (slash-expanded, semicolon-split — same shape the
// scheduler walks). Single-line, no expand/popover by design.
//
// Status icons:
//   ✓ complete    — green     — all expected sites done OR backed-off
//   ● in_progress — amber     — this term is currently being scraped
//   ◐ partial     — amber-grey — some progress but neither complete nor in-flight
//   ○ not_started — grey      — never tried this bootstrap window
//   ✕ failed      — red       — every site failed/backed-off, none completed
//
// Polling: 5 s while a scan is in flight, 30 s when idle. Matches the
// /api/scan/term-status endpoint's design.
//
// Secondary line below the list: bootstrap age + pairs done + next auto run.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { apiGet } from '../../lib/api';
import type { TermStatusEntry, TermStatusResponse } from '../../lib/types';

const COLLAPSE_KEY = 'nice2job.scan-status-bar.collapsed';

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diff = Date.now() - t;
  const past = diff >= 0;
  const ms = Math.abs(diff);
  const m = Math.floor(ms / 60000);
  if (m < 1) return past ? 'just now' : 'in <1 min';
  if (m < 60) return past ? `${m} min ago` : `in ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return past ? `${h} h ago` : `in ${h} h`;
  const d = Math.floor(h / 24);
  return past ? `${d} d ago` : `in ${d} d`;
}

function fmtUntil(iso: string | null | undefined): string {
  if (!iso) return 'soon';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'soon';
  const diff = t - Date.now();
  if (diff <= 0) return 'any moment';
  const totalMin = Math.floor(diff / 60_000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 48) return m > 0 ? `${h} h ${m} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return `~${d} d`;
}

// Status glyphs chosen so static screenshots distinguish them at a glance
// (no pulse animation visible) — colors deliberately spread across the
// hue wheel.
//   complete    → green check                 (✓ #2f8654)
//   in_progress → blue ring  (animation makes it the obvious "live" one,
//                            but the hue alone is also distinct from
//                            amber `partial` so screenshots aren't
//                            ambiguous either)
//   partial     → amber half-circle
//   failed      → red ✕
//   not_started → grey ○
function statusIcon(status: TermStatusEntry['status']): { glyph: string; color: string } {
  switch (status) {
    case 'complete':    return { glyph: '✓', color: '#2f8654' };
    case 'in_progress': return { glyph: '●', color: '#3a8dde' };
    case 'partial':     return { glyph: '◐', color: '#b88a2a' };
    case 'failed':      return { glyph: '✕', color: '#b43a3a' };
    case 'not_started':
    default:            return { glyph: '○', color: 'var(--ink3)' };
  }
}

function TermRow({ entry }: { entry: TermStatusEntry }) {
  const { glyph, color } = statusIcon(entry.status);
  const { completed, total, failed, backed_off } = entry.summary;
  const jobs = entry.total_count;
  const lastRun = entry.last_run_at;
  // Preferred count label: "<last scan new> new · <cumulative> total" from
  // scan-term-stats. Falls back to the legacy "<found> jobs" when the
  // stats file has no entry yet (terms not scanned since the feature
  // shipped). last_new can legitimately be 0 ("ran, found nothing new").
  const hasStats = entry.history_total != null && entry.last_new != null;
  const countLabel = hasStats
    ? `${entry.last_new} new · ${entry.history_total} total`
    : `${jobs} job${jobs === 1 ? '' : 's'}`;
  // Trailing context line. Keep it short. For complete / partial we lead
  // with the job count (the metric the user actually cares about), then
  // the "last scanned" timestamp.
  let trailing: string | null = null;
  if (entry.status === 'in_progress' && entry.running) {
    const r = entry.running;
    const parts: string[] = [];
    if (r.site) parts.push(`running on ${r.site}`);
    if (r.page != null && r.page_max != null) parts.push(`page ${r.page}/${r.page_max}`);
    if (r.new_in_pair != null) parts.push(`+${r.new_in_pair} new`);
    if (jobs > 0) parts.push(`${jobs} jobs so far`);
    trailing = parts.join(' · ');
  } else if (entry.status === 'complete') {
    const parts: string[] = [countLabel];
    if (lastRun) parts.push(fmtRelative(lastRun));
    trailing = parts.join(' · ');
  } else if (entry.status === 'partial') {
    const parts: string[] = [`${completed}/${total} sites · ${countLabel}`];
    if (lastRun) parts.push(fmtRelative(lastRun));
    trailing = parts.join(' · ');
  } else if (entry.status === 'failed') {
    trailing = failed > 0 ? `${failed} failed` : 'all sites failed';
  } else if (entry.status === 'not_started' && backed_off > 0) {
    trailing = `${backed_off} backed off`;
  }
  return (
    <div style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 10,
      padding: '6px 0',
      borderTop: '1px solid var(--line2)',
      fontSize: 13,
    }}>
      <span style={{
        color,
        fontWeight: 600,
        minWidth: 14,
        display: 'inline-block',
        textAlign: 'center',
        // Pulsing animation when in_progress
        animation: entry.status === 'in_progress' ? 'pulse 1.2s ease-in-out infinite' : 'none',
      }}>
        {glyph}
      </span>
      <span style={{ flex: 1, color: 'var(--ink)' }}>{entry.term}</span>
      {trailing && (
        <span style={{ color: 'var(--ink3)', fontSize: 12 }}>{trailing}</span>
      )}
    </div>
  );
}

export default function ScanStatusBar() {
  const q = useQuery({
    queryKey: ['scan-term-status'],
    queryFn: () => apiGet<TermStatusResponse>('/api/scan/term-status'),
    refetchInterval: (query) => (query.state.data?.in_flight ? 5_000 : 30_000),
    staleTime: 2_000,
  });

  // Collapsed-by-default to save space. User toggle persists across
  // navigations. Auto-expand when a scan is in flight so the user
  // doesn't miss live progress; auto-collapse when it finishes (only
  // if the user hadn't manually expanded — we tracks userOverride).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return raw == null ? true : raw === '1';
    } catch { return true; }
  });
  const persistCollapsed = (v: boolean) => {
    setCollapsed(v);
    try { localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0'); } catch { /* noop */ }
  };
  // Auto-expand on in_flight transition, but DON'T auto-collapse on idle
  // (user might still want to see the result of the last scan).
  const inFlight = !!q.data?.in_flight;
  const [autoExpandedKey, setAutoExpandedKey] = useState<string | null>(null);
  useEffect(() => {
    if (inFlight) {
      const k = q.data?.in_flight?.job_id || 'unknown';
      if (autoExpandedKey !== k && collapsed) {
        setCollapsed(false);
        setAutoExpandedKey(k);
      }
    }
  }, [inFlight, q.data?.in_flight?.job_id, collapsed, autoExpandedKey]);

  const data = q.data;
  if (!data) {
    // First-load skeleton — keep tiny so it doesn't push content around
    return (
      <div className="card" style={{ padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--ink3)' }}>
        Loading scan status…
      </div>
    );
  }

  if (!data.terms.length) {
    return (
      <div className="card" style={{ padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--ink3)' }}>
        No search terms configured yet. Add keywords below to start scanning.
      </div>
    );
  }

  // Pairs-done counter for the secondary line
  let totalPairs = 0;
  let donePairs = 0;
  let completeTerms = 0;
  let runningTerm: string | null = null;
  for (const t of data.terms) {
    totalPairs += t.summary.total;
    donePairs  += t.summary.completed + t.summary.backed_off;
    if (t.status === 'complete') completeTerms++;
    if (t.status === 'in_progress' && !runningTerm) runningTerm = t.term;
  }

  const bootstrapStarted = data.bootstrap.started_at;
  const nextAuto = data.next_action_eta_at;

  // Compact one-line summary shown in the header (always visible).
  // While in_flight: "Scanning 'AI Lecturer' · 4/12 pairs"
  // Idle:           "5 terms · 12/16 pairs done · next auto in 4 h"
  const summary = runningTerm
    ? `Scanning "${runningTerm}" · ${donePairs}/${totalPairs} pairs`
    : `${data.terms.length} term${data.terms.length === 1 ? '' : 's'} · ${completeTerms} complete · ${donePairs}/${totalPairs} pairs done`;

  return (
    <div className="card" style={{ padding: collapsed ? '8px 14px' : '10px 14px', marginBottom: 14 }}>
      {/* Pulse keyframes scoped to this card — keeps it self-contained */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.35; }
        }
      `}</style>
      {/* Header: always visible, clickable to toggle. */}
      <div
        onClick={() => persistCollapsed(!collapsed)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        title={collapsed ? 'Expand per-term progress' : 'Collapse per-term progress'}
      >
        <span style={{ color: 'var(--ink3)', display: 'flex', alignItems: 'center' }}>
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </span>
        <span className="tab" style={{ fontSize: 10.5 }}>Scan progress</span>
        <span style={{
          flex: 1,
          fontSize: 12,
          color: runningTerm ? 'var(--accent2)' : 'var(--ink2)',
          fontWeight: runningTerm ? 500 : 400,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {summary}
        </span>
      </div>

      {/* Body — only when expanded. */}
      {!collapsed && (
        <>
          <div style={{ marginTop: 6 }}>
            {data.terms.map((entry) => (
              <TermRow key={entry.term} entry={entry} />
            ))}
          </div>
          <div style={{
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px solid var(--line2)',
            fontSize: 11,
            color: 'var(--ink3)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}>
            {bootstrapStarted && (
              <span>bootstrap started {fmtRelative(bootstrapStarted)}</span>
            )}
            {nextAuto && !data.in_flight && (
              <>
                {bootstrapStarted && <span style={{ color: 'var(--line)' }}>·</span>}
                <span>next auto run {fmtUntil(nextAuto)}</span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
